import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { EhrOverview, EhrStatusDetails } from '@eluve/blocks';
import { Button, Icon, SimpleTooltip, Stack, Text } from '@eluve/components';
import { useCaptureEventOnMount } from '@eluve/posthog-react';
import type { ExternalEhr } from '@eluve/smart-blocks';
import {
  useEluveExtExists,
  useIsFeatureFlagEnabled,
} from '@eluve/smart-blocks';
import { getVendorDataMapper } from '@eluve/vendor';

import { appConfig } from '../../../../../config';
import { CHROME_WEB_STORE_URL } from '../constants';
import { useEhrOnboardingStatus } from '../hooks/useEhrOnboardingStatus';
import { useEhrStatusDetails } from '../useEhrStatusDetails';
import { useImportDataFromEhr } from '../useImportDataFromEhr';

// --- Types ---

type EhrDataImporterProps = {
  onlyShowIfError?: boolean;
};

type ImporterState =
  | 'install-extension'
  | 'complete-onboarding'
  | 'multiple-ehrs'
  | 'single-ehr'
  | 'none';

// --- State derivation ---

/**
 * Determines which UI state the importer should show.
 * Priority order matters — first match wins.
 */
function getImporterState({
  isAnyTenantLocationWebBased,
  eluveExtExists,
  shouldPromptUserToFinishOnboarding,
  externalEhrs,
}: {
  isAnyTenantLocationWebBased: boolean;
  eluveExtExists: boolean | undefined;
  shouldPromptUserToFinishOnboarding: boolean;
  externalEhrs: ExternalEhr[];
}): ImporterState {
  // 1. Tenant has web-based EHR locations but extension is not installed
  if (isAnyTenantLocationWebBased && eluveExtExists === false) {
    return 'install-extension';
  }

  // 2. User hasn't completed onboarding
  if (shouldPromptUserToFinishOnboarding) {
    return 'complete-onboarding';
  }

  // 3. User has multiple connected EHRs
  if (externalEhrs.length > 1) {
    return 'multiple-ehrs';
  }

  // 4. User has a single connected EHR
  if (externalEhrs.length === 1) {
    return 'single-ehr';
  }

  return 'none';
}

// --- Sub-components ---

const DownloadContent: React.FC = () => {
  useCaptureEventOnMount('ehr_data_importer_install_extension_prompt_shown');

  return (
    <Stack
      className="max-w-xs"
      direction="horizontal"
      justify="between"
      align="stretch"
    >
      <div className="w-10">
        <Button
          variant="outline"
          className="size-10 items-center rounded-sm border-none bg-bg-tertiary"
        >
          <Icon name="Download" size="lg" />
        </Button>
      </div>
      <Stack className="ml-3" align="stretch">
        <span className="text-text-primary sm:text-sm">
          Install the extension
        </span>
        <span className="pt-2 font-normal text-text-secondary sm:text-xs">
          Get the chrome extension to easily sync your note back to your EHR.
        </span>
      </Stack>
    </Stack>
  );
};

const OnboardingContent: React.FC = () => {
  useCaptureEventOnMount('ehr_data_importer_complete_onboarding_prompt_shown');

  return (
    <Stack
      className="max-w-xs"
      direction="horizontal"
      justify="between"
      align="stretch"
    >
      <div className="w-10">
        <Button
          variant="outline"
          className="size-10 items-center rounded-sm border-none bg-bg-tertiary"
        >
          <Icon name="ArrowRightLeft" size="lg" />
        </Button>
      </div>
      <Stack className="ml-3" align="stretch">
        <span className="text-text-primary sm:text-sm">EHR Integration</span>
        <span className="pt-2 font-normal text-text-supporting sm:text-xs">
          You're almost there! Finish setting up your integration!
        </span>
      </Stack>
    </Stack>
  );
};

const MultipleEhrIntegrationComponent: React.FC<{
  isDataImporting: boolean;
  externalEhrs: ExternalEhr[];
}> = ({ isDataImporting, externalEhrs }) => {
  const { data: ehrStatus } = useEhrStatusDetails();
  const { tenantId } = useParams() as { tenantId: string };
  const currentTenantEhrStatus =
    ehrStatus?.eluveDomains?.[appConfig.VITE_API_DOMAIN]?.tenants?.[tenantId];

  return (
    <Stack
      className="max-w-xs"
      direction="horizontal"
      align="center"
      justify="between"
    >
      <Stack align="stretch">
        <Text className="sm:text-sm">EHR Integration</Text>
        <Text className="font-light sm:text-xs">
          {!Object.keys(currentTenantEhrStatus ?? {}).length
            ? ''
            : externalEhrs.every(
                  (ehr) =>
                    currentTenantEhrStatus?.vendors?.[ehr.vendor]?.domains?.[
                      ehr.domain
                    ]?.status === 'CONNECTED',
                )
              ? `${externalEhrs.length} accounts connected`
              : 'Problems syncing with your EHR'}
        </Text>
      </Stack>
      <Icon
        name="RefreshCw"
        size="lg"
        className={isDataImporting ? 'animate-spin' : ''}
      />
    </Stack>
  );
};

const MultipleEhrIntegrationPanel: React.FC<{
  externalEhrs: ExternalEhr[];
}> = ({ externalEhrs }) => {
  const { data: ehrStatus } = useEhrStatusDetails();
  const { tenantId } = useParams() as { tenantId: string };
  const currentTenantEhrStatus =
    ehrStatus?.eluveDomains?.[appConfig.VITE_API_DOMAIN]?.tenants?.[tenantId];

  const logos = useMemo(() => {
    return externalEhrs.map((ehr) => {
      const { vendor, domain } = ehr;
      const vendorProvider = getVendorDataMapper(vendor);
      const logoData = vendorProvider.getLogo();
      const logo = (connected: boolean) => (
        <img
          src={logoData}
          alt="Logo"
          className={`${connected ? '' : 'grayscale'}`}
        />
      );
      return {
        vendor,
        domain,
        logo,
      };
    });
  }, [externalEhrs]);

  return logos.map(({ vendor, domain, logo }, index) => {
    const { error, syncedAt, status } =
      currentTenantEhrStatus?.vendors?.[vendor]?.domains?.[domain] ?? {};
    return (
      <div
        key={`multiple-accounts-overview-${vendor}-${index}`}
        className="mt-3"
      >
        <EhrOverview
          vendor={vendor}
          domain={domain}
          logo={logo}
          showStatusDetails
          error={error}
          status={status}
          syncedAt={syncedAt}
        />
      </div>
    );
  });
};

// --- Main component ---

export const EhrDataImporter: React.FC<EhrDataImporterProps> = ({
  onlyShowIfError,
}) => {
  const { tenantId } = useParams() as { tenantId: string };
  const { eluveExtExists } = useEluveExtExists();
  const isEhrOnboardingEnabled = useIsFeatureFlagEnabled('EHR_ONBOARDING');
  const areLocationsEnabled = useIsFeatureFlagEnabled('LOCATIONS');

  const {
    importDataFromEhr,
    isDataImporting,
    isImportAllowed,
    isSupportedBrowser,
  } = useImportDataFromEhr(tenantId);
  const { data: ehrStatus, refetch: syncEhrStatus } = useEhrStatusDetails();
  const [showEhrIntegrationPanel, setShowEhrIntegrationPanel] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const isUserCurrentlyOnboarding = searchParams.get('event') === 'onboarding';

  const { data: onboardingStatus } = useEhrOnboardingStatus();

  const { externalEhrs } = onboardingStatus;

  const currentTenantEhrStatus =
    ehrStatus?.eluveDomains?.[appConfig.VITE_API_DOMAIN]?.tenants?.[tenantId];

  const shouldPromptUserToFinishOnboarding =
    onboardingStatus.shouldPromptOnboarding &&
    !isUserCurrentlyOnboarding &&
    isEhrOnboardingEnabled;

  // --- Auto-open onboarding after extension installation ---

  const prevExtExists = useRef(eluveExtExists);
  useEffect(() => {
    if (
      prevExtExists.current === false &&
      eluveExtExists === true &&
      shouldPromptUserToFinishOnboarding
    ) {
      setSearchParams({ event: 'onboarding' });
    }
    prevExtExists.current = eluveExtExists;
  }, [eluveExtExists, shouldPromptUserToFinishOnboarding, setSearchParams]);

  // --- Determine state ---

  const importerState = getImporterState({
    isAnyTenantLocationWebBased: onboardingStatus.hasWebBasedEhr,
    eluveExtExists,
    shouldPromptUserToFinishOnboarding,
    externalEhrs,
  });

  // --- Derive content from state ---

  const performImport = async () => {
    await importDataFromEhr({ showToast: true });
    await syncEhrStatus();
  };

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  let content: React.ReactNode = null;
  let ehrIntegrationPanelContent: React.ReactNode = null;
  let onClick: (() => void) | null = null;
  let tooltipLabel: string | null = null;

  switch (importerState) {
    case 'install-extension':
      content = <DownloadContent />;
      tooltipLabel = 'Install the extension';
      onClick = () => openInNewTab(CHROME_WEB_STORE_URL);
      break;

    case 'complete-onboarding':
      content = <OnboardingContent />;
      tooltipLabel = 'Complete onboarding';
      onClick = () => setSearchParams({ event: 'onboarding' });
      break;

    case 'multiple-ehrs':
      content = (
        <MultipleEhrIntegrationComponent
          isDataImporting={isDataImporting}
          externalEhrs={externalEhrs}
        />
      );
      tooltipLabel = 'Import patients and appointments';
      onClick = performImport;
      ehrIntegrationPanelContent = (
        <MultipleEhrIntegrationPanel externalEhrs={externalEhrs} />
      );
      break;

    case 'single-ehr': {
      const { vendor, domain } = externalEhrs[0]!;
      const vendorProvider = getVendorDataMapper(vendor);
      const logoData = vendorProvider.getLogo();
      const logo = (connected: boolean) => (
        <img
          src={logoData}
          alt="Logo"
          className={`${connected ? '' : 'grayscale'}`}
        />
      );
      const { error, syncedAt, status } =
        currentTenantEhrStatus?.vendors?.[vendor]?.domains?.[domain] ?? {};

      content = (
        <Stack
          className="max-w-xs"
          direction="horizontal"
          align="center"
          justify="between"
        >
          <EhrOverview
            vendor={vendor}
            domain={domain}
            logo={logo}
            error={error}
            status={status}
            syncedAt={syncedAt}
          />
          <Icon
            name="RefreshCw"
            size="lg"
            className={isDataImporting ? 'animate-spin' : ''}
          />
        </Stack>
      );
      tooltipLabel = 'Import patients and appointments';
      onClick = performImport;
      ehrIntegrationPanelContent = (
        <EhrStatusDetails
          vendor={vendor}
          domain={domain}
          error={error}
          status={status}
          syncedAt={syncedAt}
        />
      );
      break;
    }
  }

  // --- Visibility ---

  const errorFound = isSupportedBrowser && eluveExtExists === false;

  const isVisible = onlyShowIfError
    ? errorFound
    : isSupportedBrowser && Boolean(content) && areLocationsEnabled;

  if (!isVisible) {
    return null;
  }

  // --- Render ---

  const cardContent = (
    <div className="rounded-lg border border-border-primary bg-transparent p-3">
      {content}
    </div>
  );

  return (
    <div
      onMouseEnter={() => {
        if (isImportAllowed) setShowEhrIntegrationPanel(true);
      }}
      onMouseLeave={() => {
        if (isImportAllowed) setShowEhrIntegrationPanel(false);
      }}
      className="w-full"
    >
      <div
        onClick={() => {
          if (onClick && !isDataImporting) onClick();
        }}
        className={onClick && !isDataImporting ? 'cursor-pointer' : ''}
      >
        {tooltipLabel ? (
          <SimpleTooltip label={tooltipLabel}>{cardContent}</SimpleTooltip>
        ) : (
          cardContent
        )}
      </div>
      {showEhrIntegrationPanel && ehrIntegrationPanelContent && (
        <div className="absolute z-50 w-72">
          <div className="h-6"></div>
          <div className="rounded-lg border border-border-primary bg-bg-primary">
            <Stack className="m-2 mx-4" align="stretch" fullWidth={false}>
              <div className="items-center">
                <Text color="tertiary" className="mt-2 mb-3 md:text-xs">
                  EHR Integration
                </Text>
              </div>
              <hr className="h-px border-0 bg-border-secondary" />
              {ehrIntegrationPanelContent}
            </Stack>
          </div>
        </div>
      )}
    </div>
  );
};
